// Iter80 — Controlled Watchlist Applicability Board.
//
// Read-only Dashboard panel that lets the operator manually evaluate
// a single scanner-ready payload envelope across the five toolbox
// strategy modules side-by-side, per ticker, in a fixed module order.
//
// Operator question answered:
//   "For each ticker in this selected envelope, which strategy modules
//    returned ENTER / WATCH / BLOCKED / NO_TRADE / ERROR / N/A?"
//
// Operator question explicitly NOT answered:
//   The board does not pick a strategy, does not declare a module
//   victor, does not produce a trade recommendation, and does not
//   execute anything. Applicability only.
//
// HARD CONSTRAINTS (asserted by Iter80 tests):
//  * Manual-run only. Zero HTTP on mount. No effect-hooks fire HTTP.
//    No recurring timer, no auto-fire on dashboard load.
//  * One outbound POST per selected module per click, targeting only
//    the EXISTING five toolbox module scanner preview endpoints.
//    Unselected modules are never called.
//  * The Market Scanner control plane and its result cache are never
//    contacted by the board.
//  * The Wheel flagship endpoint is never contacted by the board.
//  * Broker, account, position, journal, admin, trade, and order
//    endpoints are never contacted.
//  * No ranking, no module victor selection, no "top" or "preferred"
//    or "auto-select" or "trade this" language anywhere in user-facing
//    output — except inside explicit safety disclaimers such as
//    "Applicability only", "No ranking", "No winner".
//  * Wheel ITM is NOT a selectable module here. A static note
//    documents that the flagship is evaluated in its own panel.
//  * Module column order is fixed. Tickers may only be sorted
//    lexicographically (alphabetically) or by envelope order — never
//    by status quality.

import React, { useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ChevronDown, ChevronUp, Play, Eraser,
  ShieldAlert, Layers, ListChecks,
} from "lucide-react";
import { API } from "@/App";
import {
  STATUS_COLOR_MAP, STATUS_NEUTRAL_DEFAULT,
  ReasonCodeChips, ScannerWarnings, ScannerErrorMessage,
} from "@/components/ScannerResultNormalization";

// ── Selectable toolbox modules (fixed order) ──────────────────────────
//
// Wheel ITM is deliberately absent. The flagship has a different
// protected endpoint and a different decision doctrine; mixing it
// into a cross-module applicability matrix would create implicit
// ranking pressure. A static "evaluated separately" note is rendered
// below the selector instead.
//
// Each entry's `testid` is an inlined literal so static-source
// invariant scanners can locate the per-module checkbox surface
// without needing to evaluate template literals.

const MODULES = Object.freeze([
  { id: "BPCS",
    label: "BPCS",
    endpoint: "/forge/toolbox/scanner/bpcs-preview",
    testid: "applicability-board-module-checkbox-BPCS",
    accent: "border-[#00B0FF]/40 bg-[#00B0FF]/5 text-[#00B0FF]" },
  { id: "BCCS",
    label: "BCCS",
    endpoint: "/forge/toolbox/scanner/bccs-preview",
    testid: "applicability-board-module-checkbox-BCCS",
    accent: "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]" },
  { id: "IRON_CONDOR",
    label: "Iron Condor",
    endpoint: "/forge/toolbox/scanner/iron-condor-preview",
    testid: "applicability-board-module-checkbox-IRON_CONDOR",
    accent: "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]" },
  { id: "CFM_CC_SHARES",
    label: "CFM Shares",
    endpoint: "/forge/toolbox/scanner/cfm-shares-preview",
    testid: "applicability-board-module-checkbox-CFM_CC_SHARES",
    accent: "border-[#22D3CC]/40 bg-[#22D3CC]/5 text-[#22D3CC]" },
  { id: "CFM_CC_LEAPS",
    label: "CFM LEAPS",
    endpoint: "/forge/toolbox/scanner/cfm-leaps-preview",
    testid: "applicability-board-module-checkbox-CFM_CC_LEAPS",
    accent: "border-[#B084FF]/40 bg-[#B084FF]/5 text-[#B084FF]" },
]);

// ── Default envelope: structurally valid for BPCS/Iron Condor; the
// other modules will surface BLOCKED / Missing context naturally.

const DEFAULT_ENVELOPE = {
  watchlist: ["SPY"],
  payloads_by_symbol: {
    SPY: {
      spot_price: 500.0,
      wes: {
        val: 495.0, vah: 510.0, poc: 500.0,
        wes_p: 8, wes_c: 6, delta_VAL: 0.3,
        closed_bar_confirmed: true,
      },
      chain: [
        { type: "put", strike: 490.0, bid: 1.60, ask: 1.65,
          delta: -0.25, open_interest: 1500, volume: 100,
          expiration: "2026-06-21", dte: 21 },
        { type: "put", strike: 485.0, bid: 0.55, ask: 0.60,
          delta: -0.12, open_interest: 1500, volume: 80,
          expiration: "2026-06-21", dte: 21 },
      ],
      ivr: 35,
    },
  },
};

const NA_BADGE =
  "border-[#3F3F47] bg-[#121216] text-[#5F5F66]";

export default function ApplicabilityBoard() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(
    JSON.stringify(DEFAULT_ENVELOPE, null, 2)
  );
  const [parseErr, setParseErr] = useState(null);
  const [selected, setSelected] = useState(
    Object.fromEntries(MODULES.map(m => [m.id, true]))
  );
  // perModule: { [moduleId]: { loading, error, result } }
  const [perModule, setPerModule] = useState({});
  const [lastRequest, setLastRequest] = useState(null);

  function resetBody() {
    setBody(JSON.stringify(DEFAULT_ENVELOPE, null, 2));
    setParseErr(null);
  }

  function clearBoard() {
    setPerModule({});
  }

  function toggleModule(id) {
    setSelected(s => ({ ...s, [id]: !s[id] }));
  }

  async function runBoard() {
    setParseErr(null);
    let parsed;
    try {
      parsed = JSON.parse(body || "{}");
    } catch (e) {
      setParseErr(`Invalid JSON: ${e.message}`);
      return;
    }
    setLastRequest(parsed);

    // Initialize all selected modules to loading; unselected entries
    // are explicitly cleared so the matrix renders N/A for them.
    const initial = {};
    MODULES.forEach(m => {
      if (selected[m.id]) {
        initial[m.id] = { loading: true, error: null, result: null };
      }
    });
    setPerModule(initial);

    // Sequentially POST to each selected module's scanner endpoint.
    // Sequential — not Promise.all — to keep server pressure light
    // and to honour the spec's "one selected module scanner endpoint
    // call per selected module per run" contract without bursts.
    for (const m of MODULES) {
      if (!selected[m.id]) continue;
      try {
        const r = await axios.post(`${API}${m.endpoint}`, parsed);
        setPerModule(prev => ({
          ...prev,
          [m.id]: { loading: false, error: null, result: r.data },
        }));
      } catch (e) {
        setPerModule(prev => ({
          ...prev,
          [m.id]: {
            loading: false,
            error: e?.response?.data?.detail || e.message || "Network error",
            result: null,
          },
        }));
      }
    }
  }

  // Build the union of tickers that appeared in any module result
  // (or in the envelope's watchlist as a fallback). Sort
  // alphabetically — never by status quality.
  const symbols = collectSymbols(perModule, body);
  const anyRan = Object.keys(perModule).length > 0;

  return (
    <div
      data-testid="applicability-board-panel"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        data-testid="applicability-board-toggle"
        onClick={() => setOpen(v => !v)}
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Layers size={16} className="text-[#E0E0E0]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Controlled Watchlist Applicability Board
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · manual-run · applicability only · no ranking · no winner · no execution
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ───────────────────────────────────── */}
          <div
            data-testid="applicability-board-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="applicability-board-banner-manual-run">
              Manual-run only.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="applicability-board-banner-applicability-only">
              Applicability only — no ranking, no winner, no execution.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="applicability-board-banner-no-ranking">
              No ranking.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="applicability-board-banner-no-winner">
              No winner.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="applicability-board-banner-no-execution">
              No trade execution.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="applicability-board-banner-no-broker-account-fetch">
              No broker/account live-data fetch.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="applicability-board-banner-no-journal-mutation">
              No journal/database mutation.
            </span>
          </div>

          {/* ── Module selector + Wheel-protected note ──────────── */}
          <div className="px-4 py-3 border-b border-[#26262B] flex items-start gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <ListChecks size={12} className="text-[#5F5F66]" />
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                Modules
              </span>
            </div>
            <div
              data-testid="applicability-board-module-selector"
              className="flex items-center gap-3 flex-wrap"
            >
              {MODULES.map(m => (
                <label
                  key={m.id}
                  data-testid={m.testid}
                  className="inline-flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!selected[m.id]}
                    onChange={() => toggleModule(m.id)}
                    className="accent-[#E0E0E0]"
                  />
                  <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-[0.14em] ${m.accent}`}>
                    {m.label}
                  </span>
                </label>
              ))}
            </div>
            <div
              data-testid="applicability-board-wheel-protected-note"
              className="ml-auto text-[9px] font-mono uppercase tracking-[0.12em] text-[#00FF88]/70 border border-[#00FF88]/20 bg-[#00FF88]/5 px-2 py-1 rounded-sm"
            >
              Wheel ITM remains protected flagship and is evaluated separately in its own visibility panel.
            </div>
          </div>

          {/* ── Envelope editor ─────────────────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B]">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                Scanner-ready envelope (watchlist + payloads_by_symbol)
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="applicability-board-reset"
                  onClick={resetBody}
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  <Eraser size={10} /> Reset
                </button>
                <button
                  type="button"
                  data-testid="applicability-board-clear"
                  onClick={clearBoard}
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  Clear board
                </button>
                <button
                  type="button"
                  data-testid="applicability-board-run"
                  onClick={runBoard}
                  className="px-3 py-1 rounded-sm border border-[#E0E0E0]/30 bg-[#E0E0E0]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#E0E0E0] hover:bg-[#E0E0E0]/20 inline-flex items-center gap-1"
                >
                  <Play size={10} /> Run Applicability Board
                </button>
              </div>
            </div>

            <details
              data-testid="applicability-board-advanced-payload"
              className="border border-[#26262B] rounded-sm"
            >
              <summary
                data-testid="applicability-board-advanced-summary"
                className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]"
              >
                Advanced / Developer payload (raw JSON)
              </summary>
              <textarea
                data-testid="applicability-board-payload"
                value={body}
                onChange={e => setBody(e.target.value)}
                spellCheck={false}
                className="w-full bg-[#08080A] border-t border-[#26262B] p-2 text-[10px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#E0E0E0]/50 leading-relaxed"
                rows={8}
              />
            </details>

            {parseErr && (
              <div
                data-testid="applicability-board-parse-error"
                className="mt-2 flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
              >
                <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                <span>{parseErr}</span>
              </div>
            )}
          </div>

          {/* ── Empty / Matrix ──────────────────────────────────── */}
          {!anyRan && (
            <div
              data-testid="applicability-board-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No applicability run yet. Select modules above, edit the
              envelope if needed, then click Run Applicability Board.
            </div>
          )}

          {anyRan && (
            <BoardMatrix
              symbols={symbols}
              perModule={perModule}
              selected={selected}
              lastRequest={lastRequest}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function collectSymbols(perModule, bodyText) {
  const set = new Set();
  Object.values(perModule).forEach(slot => {
    if (slot?.result?.results) {
      slot.result.results.forEach(r => r?.symbol && set.add(r.symbol));
    }
  });
  if (set.size === 0) {
    try {
      const parsed = JSON.parse(bodyText || "{}");
      if (Array.isArray(parsed.watchlist)) {
        parsed.watchlist.forEach(s => typeof s === "string" && set.add(s));
      }
    } catch (e) {
      // ignore — empty list is fine.
    }
  }
  return Array.from(set).sort();
}

function findRowForSymbol(slot, symbol) {
  if (!slot?.result?.results) return null;
  return slot.result.results.find(r => r?.symbol === symbol) || null;
}

function pickHeader(perModule, symbol) {
  // Use the first available row across modules to surface
  // header context (spot, regime, wes_p/wes_c, ivr). Pure read.
  for (const slot of Object.values(perModule)) {
    const row = findRowForSymbol(slot, symbol);
    if (row) return row;
  }
  return null;
}

function BoardMatrix({ symbols, perModule, selected, lastRequest }) {
  return (
    <div
      data-testid="applicability-board-matrix"
      className="px-4 py-3 space-y-3"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
        Applicability only — no ranking, no winner, no execution.
      </div>
      {symbols.length === 0 && (
        <div
          data-testid="applicability-board-no-symbols"
          className="text-[10px] font-mono text-[#5F5F66]"
        >
          No symbols resolved from the envelope or module results.
        </div>
      )}
      {symbols.map(sym => (
        <BoardRow
          key={sym}
          symbol={sym}
          perModule={perModule}
          selected={selected}
        />
      ))}
    </div>
  );
}

function BoardRow({ symbol, perModule, selected }) {
  const header = pickHeader(perModule, symbol);
  return (
    <div
      data-testid={`applicability-board-row-${symbol}`}
      className="rounded-sm border border-[#26262B] bg-[#08080A] p-3"
    >
      <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono mb-2">
        <span
          data-testid={`applicability-board-row-symbol-${symbol}`}
          className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#E0E0E0]"
        >
          {symbol}
        </span>
        {header?.spot_price != null && (
          <span
            data-testid={`applicability-board-row-spot-${symbol}`}
            className="text-[#8A8A93]"
          >
            SPOT <span className="text-[#E0E0E0] font-bold">{header.spot_price}</span>
          </span>
        )}
        {header?.regime && (
          <span
            data-testid={`applicability-board-row-regime-${symbol}`}
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 text-[9px] uppercase tracking-[0.12em] text-[#FFB800]"
          >
            {header.regime}
          </span>
        )}
        {header?.wes_p != null && (
          <span
            data-testid={`applicability-board-row-wes-p-${symbol}`}
            className="text-[#8A8A93]"
          >
            WES-P <span className="text-[#E0E0E0] font-bold">{header.wes_p}</span>
          </span>
        )}
        {header?.wes_c != null && (
          <span
            data-testid={`applicability-board-row-wes-c-${symbol}`}
            className="text-[#8A8A93]"
          >
            WES-C <span className="text-[#E0E0E0] font-bold">{header.wes_c}</span>
          </span>
        )}
        {header?.ivr != null && (
          <span
            data-testid={`applicability-board-row-ivr-${symbol}`}
            className="text-[#8A8A93]"
          >
            IVR <span className="text-[#E0E0E0] font-bold">{header.ivr}</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {MODULES.map(m => (
          <ModuleCell
            key={m.id}
            module={m}
            symbol={symbol}
            slot={perModule[m.id]}
            isSelected={!!selected[m.id]}
          />
        ))}
      </div>
    </div>
  );
}

function ModuleCell({ module: m, symbol, slot, isSelected }) {
  const baseTid = `applicability-board-cell-${m.id}-${symbol}`;

  // Module was not selected for this run → N/A passthrough.
  if (!isSelected) {
    return (
      <div
        data-testid={baseTid}
        className={`rounded-sm border ${m.accent} p-2 text-[10px] font-mono`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-bold uppercase tracking-[0.12em]">
            {m.label}
          </span>
          <span
            data-testid={`${baseTid}-status`}
            className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${NA_BADGE}`}
          >
            N/A
          </span>
        </div>
        <div className="text-[#5F5F66]">Module not selected for this run.</div>
      </div>
    );
  }

  if (!slot) {
    // Selected but no slot yet — defensive N/A.
    return (
      <div
        data-testid={baseTid}
        className={`rounded-sm border ${m.accent} p-2 text-[10px] font-mono`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-bold uppercase tracking-[0.12em]">
            {m.label}
          </span>
          <span
            data-testid={`${baseTid}-status`}
            className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${NA_BADGE}`}
          >
            N/A
          </span>
        </div>
      </div>
    );
  }

  if (slot.loading) {
    return (
      <div
        data-testid={baseTid}
        className={`rounded-sm border ${m.accent} p-2 text-[10px] font-mono`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-bold uppercase tracking-[0.12em]">
            {m.label}
          </span>
          <span
            data-testid={`${baseTid}-loading`}
            className="text-[#5F5F66] uppercase tracking-[0.12em]"
          >
            Scanning…
          </span>
        </div>
      </div>
    );
  }

  if (slot.error) {
    return (
      <div
        data-testid={baseTid}
        className={`rounded-sm border ${m.accent} p-2 text-[10px] font-mono`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-bold uppercase tracking-[0.12em]">
            {m.label}
          </span>
          <span
            data-testid={`${baseTid}-status`}
            className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${STATUS_COLOR_MAP.ERROR}`}
          >
            ERROR
          </span>
        </div>
        <div
          data-testid={`${baseTid}-error`}
          className="text-[#FF3366] break-words"
        >
          {slot.error}
        </div>
      </div>
    );
  }

  const row = findRowForSymbol(slot, symbol);

  // The module ran but did not return a row for this symbol →
  // explicit "missing context" state. Never synthesized.
  if (!row) {
    return (
      <div
        data-testid={baseTid}
        className={`rounded-sm border ${m.accent} p-2 text-[10px] font-mono`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-bold uppercase tracking-[0.12em]">
            {m.label}
          </span>
          <span
            data-testid={`${baseTid}-status`}
            className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${NA_BADGE}`}
          >
            N/A
          </span>
        </div>
        <div
          data-testid={`${baseTid}-missing-context`}
          className="text-[#5F5F66]"
        >
          Missing module context for this ticker.
        </div>
      </div>
    );
  }

  const statusClass = STATUS_COLOR_MAP[row.status] || STATUS_NEUTRAL_DEFAULT;
  const showDecision = row.decision && row.decision !== row.status;

  return (
    <div
      data-testid={baseTid}
      className={`rounded-sm border ${m.accent} p-2 text-[10px] font-mono`}
    >
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <span className="font-bold uppercase tracking-[0.12em]">
          {m.label}
        </span>
        <span
          data-testid={`${baseTid}-status`}
          className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${statusClass}`}
        >
          {row.status}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-[#8A8A93] mb-1">
        {showDecision && (
          <span
            data-testid={`${baseTid}-decision`}
            className="uppercase tracking-[0.12em]"
          >
            {row.decision}
          </span>
        )}
        {row.confidence_score != null && (
          <span data-testid={`${baseTid}-conf`}>
            CONF <span className="text-[#E0E0E0] font-bold">{row.confidence_score}</span>
          </span>
        )}
        {row.risk_score != null && (
          <span data-testid={`${baseTid}-risk`} className="text-[#FFB800]">
            RISK <span className="font-bold">{row.risk_score}</span>
          </span>
        )}
      </div>

      {/* Iter80.1 — Visible selected-structure summary chips. The
          full JSON still lives in the collapsed Details drawer below
          so nothing is lost. The chips never synthesize fields —
          when the backend evaluator did not return a structure, an
          explicit "unavailable" note is rendered instead. */}
      <StructureSummary
        module={m}
        row={row}
        testid={`${baseTid}-structure-summary`}
      />

      <details
        data-testid={`${baseTid}-details`}
        className="mt-1"
      >
        <summary className="cursor-pointer text-[9px] uppercase tracking-[0.14em] text-[#5F5F66] hover:text-[#E0E0E0]">
          Details
        </summary>
        <div className="mt-1 space-y-1">
          {Array.isArray(row.reason_codes) && row.reason_codes.length > 0 && (
            <ReasonCodeChips
              codes={row.reason_codes}
              testid={`${baseTid}-reasons`}
            />
          )}
          {Array.isArray(row.warnings) && row.warnings.length > 0 && (
            <ScannerWarnings
              warnings={row.warnings}
              testid={`${baseTid}-warnings`}
            />
          )}
          {row.error_message && (
            <ScannerErrorMessage
              message={row.error_message}
              testid={`${baseTid}-error-message`}
            />
          )}
          {(row.plain_english || row.summary) && (
            <div
              data-testid={`${baseTid}-plain`}
              className="text-[10px] font-mono text-[#E0E0E0] leading-relaxed"
            >
              {row.plain_english || row.summary}
            </div>
          )}
          {(row.selected_structure || row.option_plan) && (
            <details
              data-testid={`${baseTid}-structure`}
              className="mt-1"
            >
              <summary className="cursor-pointer text-[9px] uppercase tracking-[0.14em] text-[#5F5F66] hover:text-[#E0E0E0]">
                Selected structure / option plan
              </summary>
              <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-48 overflow-auto">
                {JSON.stringify(row.selected_structure || row.option_plan, null, 2)}
              </pre>
            </details>
          )}
          <details
            data-testid={`${baseTid}-raw`}
            className="mt-1"
          >
            <summary className="cursor-pointer text-[9px] uppercase tracking-[0.14em] text-[#5F5F66] hover:text-[#E0E0E0]">
              Raw module row JSON
            </summary>
            <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-48 overflow-auto">
              {JSON.stringify(row, null, 2)}
            </pre>
          </details>
        </div>
      </details>
    </div>
  );
}

// ── Iter80.1 — Visible selected-structure summary ─────────────────────
//
// Each module gets a compact, read-only chip strip that surfaces the
// already-computed fields from `selected_structure` / `option_plan`.
// The summary helper NEVER:
//  * synthesizes strikes, bids, asks, deltas, or any other chain field;
//  * compares modules against each other;
//  * declares a winner, a top choice, or a preferred strategy;
//  * scores or ranks the structure.
//
// When the backend evaluator did not return a structure, a single
// context-aware unavailable note is rendered — never synthesized chips.

function StructureSummary({ module: m, row, testid }) {
  const struct = row?.selected_structure || row?.option_plan;
  if (!struct || typeof struct !== "object") {
    return (
      <UnavailableNote
        moduleId={m.id}
        row={row}
        testid={testid}
      />
    );
  }
  return (
    <div
      data-testid={testid}
      className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-mono text-[#E0E0E0]"
    >
      {m.id === "BPCS"          && <SpreadStructureChips struct={struct} kind="put"  testidPrefix={testid} />}
      {m.id === "BCCS"          && <SpreadStructureChips struct={struct} kind="call" testidPrefix={testid} />}
      {m.id === "IRON_CONDOR"   && <IronCondorStructureChips struct={struct} testidPrefix={testid} />}
      {m.id === "CFM_CC_SHARES" && <CFMSharesStructureChips struct={struct} testidPrefix={testid} />}
      {m.id === "CFM_CC_LEAPS"  && <CFMLeapsStructureChips  struct={struct} testidPrefix={testid} />}
    </div>
  );
}

function UnavailableNote({ moduleId, row, testid }) {
  const reasons = Array.isArray(row?.reason_codes) ? row.reason_codes : [];
  const isMissingPayload = reasons.includes("MISSING_PAYLOAD");
  const leapsTokens = reasons.some(
    r => typeof r === "string" &&
         (r.startsWith("LEAPS_") || r.startsWith("SHORT_") ||
          r === "DELTA_INVERSION" || r === "NO_ROLL_CANDIDATES")
  );
  let msg;
  if (moduleId === "CFM_CC_SHARES") {
    msg = "Missing inventory context — CFM unavailable";
  } else if (moduleId === "CFM_CC_LEAPS") {
    msg = leapsTokens
      ? "Missing LEAPS context — PMCC unavailable"
      : "Missing LEAPS context — PMCC unavailable";
  } else if (isMissingPayload) {
    msg = "Missing option chain — structure unavailable";
  } else {
    msg = "Structure unavailable";
  }
  return (
    <div
      data-testid={testid}
      className="mt-1 text-[10px] font-mono text-[#5F5F66] italic"
    >
      {msg}
    </div>
  );
}

function StructChip({ label, value, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1"
    >
      <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
        {label}
      </span>
      <span className="font-bold">{String(value)}</span>
    </span>
  );
}

function SpreadStructureChips({ struct, kind, testidPrefix }) {
  // BPCS uses short_leg/long_leg; the evaluator already enforces type.
  const shortLeg = struct.short_leg || {};
  const longLeg  = struct.long_leg  || {};
  const k = kind === "call" ? "C" : "P";
  return (
    <>
      <StructChip
        label={`SHORT ${k}`}
        value={shortLeg.strike != null ? shortLeg.strike : null}
        testid={`${testidPrefix}-short-strike`}
      />
      <StructChip
        label={`LONG ${k}`}
        value={longLeg.strike != null ? longLeg.strike : null}
        testid={`${testidPrefix}-long-strike`}
      />
      <StructChip
        label="DTE"
        value={struct.dte != null ? struct.dte : null}
        testid={`${testidPrefix}-dte`}
      />
      <StructChip
        label="CR"
        value={struct.limit_credit != null ? struct.limit_credit : null}
        testid={`${testidPrefix}-credit`}
      />
      <StructChip
        label="MAX_PROFIT"
        value={struct.max_profit != null ? struct.max_profit : null}
        testid={`${testidPrefix}-max-profit`}
      />
      <StructChip
        label="MAX_LOSS"
        value={struct.max_loss != null ? struct.max_loss : null}
        testid={`${testidPrefix}-max-loss`}
      />
      <StructChip
        label="BE"
        value={struct.breakeven != null ? struct.breakeven : null}
        testid={`${testidPrefix}-breakeven`}
      />
      <StructChip
        label="ROR"
        value={struct.roi_on_max_risk != null ? struct.roi_on_max_risk : null}
        testid={`${testidPrefix}-ror`}
      />
    </>
  );
}

function IronCondorStructureChips({ struct, testidPrefix }) {
  const sp = struct.short_put  || {};
  const lp = struct.long_put   || {};
  const sc = struct.short_call || {};
  const lc = struct.long_call  || {};
  return (
    <>
      <StructChip
        label="P-SHORT"
        value={sp.strike != null ? sp.strike : null}
        testid={`${testidPrefix}-put-short`}
      />
      <StructChip
        label="P-LONG"
        value={lp.strike != null ? lp.strike : null}
        testid={`${testidPrefix}-put-long`}
      />
      <StructChip
        label="C-SHORT"
        value={sc.strike != null ? sc.strike : null}
        testid={`${testidPrefix}-call-short`}
      />
      <StructChip
        label="C-LONG"
        value={lc.strike != null ? lc.strike : null}
        testid={`${testidPrefix}-call-long`}
      />
      <StructChip
        label="DTE"
        value={struct.dte != null ? struct.dte : null}
        testid={`${testidPrefix}-dte`}
      />
      <StructChip
        label="TOTAL_CR"
        value={struct.total_credit != null ? struct.total_credit : null}
        testid={`${testidPrefix}-total-credit`}
      />
      <StructChip
        label="MAX_PROFIT"
        value={struct.max_profit != null ? struct.max_profit : null}
        testid={`${testidPrefix}-max-profit`}
      />
      <StructChip
        label="MAX_LOSS"
        value={struct.max_loss != null ? struct.max_loss : null}
        testid={`${testidPrefix}-max-loss`}
      />
      <StructChip
        label="LBE"
        value={struct.lower_breakeven != null ? struct.lower_breakeven : null}
        testid={`${testidPrefix}-lower-be`}
      />
      <StructChip
        label="UBE"
        value={struct.upper_breakeven != null ? struct.upper_breakeven : null}
        testid={`${testidPrefix}-upper-be`}
      />
    </>
  );
}

function CFMSharesStructureChips({ struct, testidPrefix }) {
  const sc = struct.short_call || {};
  return (
    <>
      <StructChip
        label="SHORT_C"
        value={sc.strike != null ? sc.strike : null}
        testid={`${testidPrefix}-short-call-strike`}
      />
      <StructChip
        label="DTE"
        value={struct.dte != null ? struct.dte : null}
        testid={`${testidPrefix}-dte`}
      />
      <StructChip
        label="CR"
        value={struct.call_credit != null ? struct.call_credit : null}
        testid={`${testidPrefix}-credit`}
      />
      <StructChip
        label="EXTR"
        value={struct.extrinsic_value != null ? struct.extrinsic_value : null}
        testid={`${testidPrefix}-extrinsic`}
      />
      <StructChip
        label="EXIT @"
        value={struct.assignment_exit_price != null ? struct.assignment_exit_price : null}
        testid={`${testidPrefix}-exit`}
      />
      <StructChip
        label="CUSHION%"
        value={struct.premium_cushion_pct != null ? struct.premium_cushion_pct : null}
        testid={`${testidPrefix}-cushion`}
      />
    </>
  );
}

function CFMLeapsStructureChips({ struct, testidPrefix }) {
  const leaps = struct.leaps_leg || {};
  const short = struct.short_leg || {};
  return (
    <>
      <StructChip
        label="LEAPS"
        value={leaps.strike != null
                ? `${leaps.strike}${leaps.dte != null ? ` (${leaps.dte}d)` : ""}`
                : null}
        testid={`${testidPrefix}-leaps-leg`}
      />
      <StructChip
        label="SHORT"
        value={short.strike != null
                ? `${short.strike}${short.dte != null ? ` (${short.dte}d)` : ""}`
                : null}
        testid={`${testidPrefix}-short-leg`}
      />
      <StructChip
        label="θ-CARRY"
        value={struct.theta_carry_ratio != null ? struct.theta_carry_ratio : null}
        testid={`${testidPrefix}-theta-carry`}
      />
      <StructChip
        label="NETΔ"
        value={struct.net_delta != null ? struct.net_delta : null}
        testid={`${testidPrefix}-net-delta`}
      />
      <StructChip
        label="BASIS"
        value={struct.adjusted_leaps_basis != null ? struct.adjusted_leaps_basis : null}
        testid={`${testidPrefix}-adjusted-basis`}
      />
      <StructChip
        label="ARISK"
        value={struct.assignment_acceptable === true
                ? "OK"
                : struct.assignment_acceptable === false ? "FLAG" : null}
        testid={`${testidPrefix}-assignment-risk`}
      />
    </>
  );
}
